import Api from '../../Services/Api';
import { format, differenceInDays, addDays } from 'date-fns';

/**
 * Column name mapping for API requests
 */
export const columnMapping = {
  'payout': 'epc',
  'revenue': 'estimated_revenue',
  'impressions': 'visits',
  'campaign_id': 'subid_2',
  'site_name': 'subid_3',
  'roas': 'roas',
  'profit': 'profit',
  'cost': 'cost',
  'ctr': 'ctr'
};

/**
 * Helper function to convert frontend column names to backend column names
 */
export const mapColumnName = (frontendColumnName) => {
  return columnMapping[frontendColumnName] || frontendColumnName;
};

/**
 * Fetch AdSense revenue data from real-time API
 */
export const fetchAdSenseRevenue = async (startDate, endDate) => {
  try {
    const formattedStart = startDate instanceof Date ? format(startDate, 'yyyy-MM-dd') : startDate;
    const formattedEnd = endDate instanceof Date ? format(endDate, 'yyyy-MM-dd') : endDate;

    console.log(`📊 Fetching AdSense revenue for ${formattedStart} to ${formattedEnd}`);

    const response = await Api.getAdSenseCampaignStats(formattedStart, formattedEnd);

    if (response.success && response.data) {
      console.log(`✅ AdSense data fetched: ${response.data.length} campaigns`);
      return response.data;
    } else {
      console.warn('⚠️ AdSense fetch failed or returned no data:', response.error);
      return [];
    }
  } catch (error) {
    console.error('❌ Error fetching AdSense revenue:', error);
    return [];
  }
};

/**
 * Fetch Facebook cost data
 */
export const fetchFacebookCost = async (startDate, endDate, forceRefresh = false, accountId = null) => {
  try {
    console.log(`💰 Fetching Facebook cost for date range`);

    const response = await Api.getFacebookCampaignMetrics(startDate, endDate, forceRefresh, accountId);

    if (response?.success && response.data) {
      console.log(`✅ Facebook cost data: ${response.data.length} campaigns`);
      return response.data;
    } else {
      console.warn('⚠️ Facebook cost fetch failed or returned no data');
      return [];
    }
  } catch (error) {
    console.error('❌ Error fetching Facebook cost:', error);
    return [];
  }
};

/**
 * Fetch Compado revenue data
 */
export const fetchCompadoRevenue = async (startDate, endDate) => {
  try {
    const formattedStart = startDate instanceof Date ? format(startDate, 'yyyy-MM-dd') : startDate;
    const formattedEnd = endDate instanceof Date ? format(endDate, 'yyyy-MM-dd') : endDate;

    console.log(`📊 Fetching Compado revenue for ${formattedStart} to ${formattedEnd}`);

    const response = await Api.getCompadoByCampaign({
      start_date: formattedStart,
      end_date: formattedEnd
    });

    if (response.success && response.data) {
      console.log(`✅ Compado data fetched: ${response.data.length} campaigns`);
      return response.data;
    } else {
      console.warn('⚠️ Compado fetch failed or returned no data');
      return [];
    }
  } catch (error) {
    console.error('❌ Error fetching Compado revenue:', error);
    return [];
  }
};

/**
 * Combine all data sources: Facebook cost + AdSense revenue + Compado revenue
 *
 * A campaign appears in the table if it has:
 * - Facebook cost > 0, OR
 * - AdSense revenue > 0, OR
 * - Compado revenue > 0
 */
export const combineAllData = (facebookCostData, adSenseData, compadoData, facebookCampaigns = {}) => {
  console.log('🔄 Combining all data sources:', {
    facebookCost: facebookCostData?.length || 0,
    adSense: adSenseData?.length || 0,
    compado: compadoData?.length || 0,
    facebookCampaigns: Object.keys(facebookCampaigns).length
  });

  // Create maps for quick lookup
  const facebookCostMap = {};
  const adSenseMap = {};
  const compadoMap = {};
  const allCampaignIds = new Set();

  // Process Facebook cost data
  if (facebookCostData && facebookCostData.length) {
    facebookCostData.forEach(campaign => {
      const campaignId = campaign.campaign_id;
      if (campaignId) {
        facebookCostMap[campaignId] = {
          spent: parseFloat(campaign.spent || 0),
          impressions: parseInt(campaign.impressions || 0, 10),
          clicks: parseInt(campaign.clicks || 0, 10),
          campaign_name: campaign.campaign_name,
          account_id: campaign.account_id,
          daily_budget: campaign.daily_budget ? parseFloat(campaign.daily_budget) : null,
          lifetime_budget: campaign.lifetime_budget ? parseFloat(campaign.lifetime_budget) : null,
          budget_remaining: campaign.budget_remaining ? parseFloat(campaign.budget_remaining) : null
        };
        allCampaignIds.add(campaignId);
      }
    });
  }

  // Process AdSense data
  if (adSenseData && adSenseData.length) {
    adSenseData.forEach(campaign => {
      const campaignId = campaign.campaign_id;
      if (campaignId) {
        adSenseMap[campaignId] = {
          revenue: parseFloat(campaign.revenue || 0),
          impressions: parseInt(campaign.impressions || 0, 10),
          clicks: parseInt(campaign.clicks || 0, 10)
        };
        allCampaignIds.add(campaignId);
      }
    });
  }

  // Process Compado data
  if (compadoData && compadoData.length) {
    compadoData.forEach(campaign => {
      const campaignId = campaign.campaign_id;
      if (campaignId) {
        compadoMap[campaignId] = {
          revenue: parseFloat(campaign.revenue || 0),
          clicks: parseInt(campaign.clicks || 0, 10)
        };
        allCampaignIds.add(campaignId);
      }
    });
  }

  console.log(`📝 Total unique campaign IDs found: ${allCampaignIds.size}`);

  // Build combined campaigns array
  const campaigns = [];

  allCampaignIds.forEach(campaignId => {
    const fbCost = facebookCostMap[campaignId] || null;
    const adSense = adSenseMap[campaignId] || null;
    const compado = compadoMap[campaignId] || null;

    // Calculate totals
    const cost = fbCost?.spent || 0;
    const adSenseRevenue = adSense?.revenue || 0;
    const compadoRevenue = compado?.revenue || 0;
    const totalRevenue = adSenseRevenue + compadoRevenue;

    // Skip campaigns with no cost and no revenue
    if (cost === 0 && totalRevenue === 0) {
      return;
    }

    // Use Facebook metrics for impressions/clicks (traffic source)
    const impressions = fbCost?.impressions || 0;
    const clicks = fbCost?.clicks || 0;

    // AdSense metrics (demand side)
    const demandImpressions = adSense?.impressions || 0;
    const demandClicks = adSense?.clicks || (compado?.clicks || 0);

    // Calculate derived metrics
    const profit = parseFloat((totalRevenue - cost).toFixed(2));
    const roas = cost > 0 ? parseFloat((totalRevenue / cost).toFixed(2)) : 0;
    const ctr = impressions > 0 ? parseFloat(((clicks / impressions) * 100).toFixed(2)) : 0;
    const payout = demandClicks > 0 ? parseFloat((totalRevenue / demandClicks).toFixed(4)) : 0;

    // Get campaign name from Facebook data or campaign ID
    const campaignName = fbCost?.campaign_name ||
                         facebookCampaigns[campaignId]?.name ||
                         campaignId;

    // Get account_id
    const accountId = fbCost?.account_id ||
                      facebookCampaigns[campaignId]?.account_id ||
                      null;

    campaigns.push({
      campaign_id: campaignId,
      campaign_name: campaignName,
      platform: 'facebook',
      cost: cost,
      revenue: totalRevenue,
      adsense_revenue: adSenseRevenue,
      compado_revenue: compadoRevenue,
      impressions: impressions,
      clicks: clicks,
      demand_visits: demandImpressions,
      demand_clicks: demandClicks,
      facebook_clicks: clicks,
      profit: profit,
      roas: roas,
      ctr: ctr,
      payout: payout,
      account_id: accountId,
      daily_budget: fbCost?.daily_budget || null,
      lifetime_budget: fbCost?.lifetime_budget || null,
      budget_remaining: fbCost?.budget_remaining || null
    });
  });

  console.log(`✅ Combined ${campaigns.length} campaigns with activity`);

  return {
    campaigns: campaigns,
    total: campaigns.length
  };
};

/**
 * Check if date range matches today, yesterday, or common presets
 */
export const getActiveDateFilter = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return "custom";
  }

  const today = new Date();
  const yesterday = addDays(today, -1);

  const formatDate = (date) => format(date, 'yyyy-MM-dd');
  const startFormatted = formatDate(startDate);
  const endFormatted = formatDate(endDate);
  const todayFormatted = formatDate(today);
  const yesterdayFormatted = formatDate(yesterday);

  if (startFormatted === todayFormatted && endFormatted === todayFormatted) {
    return "today";
  }

  if (startFormatted === yesterdayFormatted && endFormatted === yesterdayFormatted) {
    return "yesterday";
  }

  if (endFormatted === todayFormatted) {
    const days = differenceInDays(endDate, startDate) + 1;
    if (days === 3) return "last3days";
    if (days === 7) return "last7days";
    if (days === 14) return "last14days";
    if (days === 30) return "last30days";
  }

  return "custom";
};

/**
 * Create payload for exporting to CSV
 */
export const createExportPayload = (
  startDate,
  endDate,
  campaignFilter,
  isSubid3View,
  selectedSubid2,
  sortField,
  sortDirection,
  groupBy
) => {
  const mappedSortField = mapColumnName(sortField);

  return {
    start_date: format(startDate, 'yyyy-MM-dd'),
    end_date: format(endDate, 'yyyy-MM-dd'),
    campaign_filter: campaignFilter,
    sort_field: mappedSortField,
    sort_direction: sortDirection,
    format: 'csv',
    filename: `campaign-analytics-${format(new Date(), 'yyyy-MM-dd')}`
  };
};

// ============================================================
// DEPRECATED - Keeping for backwards compatibility during transition
// These functions are no longer used but kept to avoid breaking imports
// ============================================================

/**
 * @deprecated Use fetchAdSenseRevenue instead
 */
export const fetchRevenueData = async () => {
  console.warn('fetchRevenueData is deprecated - use fetchAdSenseRevenue instead');
  return { campaigns: [], total: 0 };
};

/**
 * @deprecated Use fetchFacebookCost instead
 */
export const fetchCostWithDates = async (start, end, forceRefresh = false, platformFilter = null, facebookAccountId = null) => {
  console.warn('fetchCostWithDates is deprecated - use fetchFacebookCost instead');

  // For backwards compatibility, return the expected format
  const facebookCost = await fetchFacebookCost(start, end, forceRefresh, facebookAccountId);

  return {
    taboola: [],
    facebook: facebookCost
  };
};

/**
 * @deprecated Use combineAllData instead
 */
export const combineRevenueAndCostData = (revenueData, taboolaCostData, facebookCostData = [], facebookCampaigns = {}, compadoData = []) => {
  console.warn('combineRevenueAndCostData is deprecated - use combineAllData instead');
  return { campaigns: [] };
};

/**
 * @deprecated No longer used - Taboola removed
 */
export const fetchTaboolaSiteMetrics = async () => {
  console.warn('fetchTaboolaSiteMetrics is deprecated - Taboola has been removed');
  return { success: false, sites: [] };
};

/**
 * @deprecated No longer used - Taboola removed
 */
export const combineSiteData = (siteMetrics, revenueData) => {
  console.warn('combineSiteData is deprecated - Taboola has been removed');
  return revenueData;
};

/**
 * @deprecated No longer used - ads.com removed
 */
export const buildFilters = () => {
  console.warn('buildFilters is deprecated - ads.com has been removed');
  return [];
};
